import { useMemo } from 'react';
import { ArrowsClockwise, CircleNotch } from '@phosphor-icons/react';
import { useFeed } from '@/hooks/useTimeline';
import { useCatalogs } from '@/hooks/useTasks';
import { useAppStore } from '@/store/appStore';
import { TimelineList } from '@/components/timeline/TimelineList';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import type { TimelineEventoTipo, FeedItem } from '@/types';

// Cuerpo reutilizable del feed de actividad del equipo: barra de filtros
// (usuario + tipo + refrescar) sobre la lista con scroll infinito. Lo usan
// tanto la FeedPage (ruta) como el modal de Actividad (campana del TopBar).
//
// Llena el alto de su contenedor (h-full flex-col); el padre decide la altura.
export function FeedPanel({ onTaskClick }: { onTaskClick?: (id: number) => void }) {
  const { data: catalogs } = useCatalogs();
  const selectTask = useAppStore((s) => s.selectTask);
  const handleTaskClick = onTaskClick ?? selectTask;

  const idColFilter = useAppStore((s) => s.feedIdColFilter);
  const setIdColFilter = useAppStore((s) => s.setFeedIdColFilter);
  const tipoFilter = useAppStore((s) => s.feedTipoFilter);
  const setTipoFilter = useAppStore((s) => s.setFeedTipoFilter);

  const filters = useMemo(() => ({
    idCol: idColFilter ?? undefined,
    tipo: (tipoFilter ?? undefined) as TimelineEventoTipo | undefined,
  }), [idColFilter, tipoFilter]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
    isFetching,
  } = useFeed(filters);

  const items: FeedItem[] = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data],
  );

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="shrink-0 flex items-center gap-2 flex-wrap px-4 py-2.5 border-b border-stone-200 bg-white">
        <div className="w-52">
          <SearchableSelect<number>
            options={(catalogs?.users ?? []).map((u) => ({
              value: u.idCol,
              label: u.nombreCompleto || u.nombre || `#${u.idCol}`,
            }))}
            value={idColFilter ?? null}
            onChange={(v) => setIdColFilter(v)}
            placeholder="Todos los usuarios"
          />
        </div>

        <div className="w-44">
          <SearchableSelect<string>
            options={[
              { value: 'creacion', label: 'Creaciones' },
              { value: 'comentario', label: 'Comentarios' },
              { value: 'cambio', label: 'Cambios' },
            ]}
            value={tipoFilter ?? null}
            onChange={(v) => setTipoFilter(v)}
            placeholder="Todo tipo"
          />
        </div>

        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          title="Actualizar"
          className="ml-auto p-1.5 rounded hover:bg-stone-100 text-brand-gray-400 hover:text-brand-gray-500 disabled:opacity-50"
        >
          <ArrowsClockwise size={16} weight="bold" className={isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto bg-brand-bg">
        <div className="max-w-3xl mx-auto px-6 py-5">
          {isLoading ? (
            <p className="text-center text-stone-500 py-10">Cargando actividad…</p>
          ) : (
            <>
              <TimelineList
                eventos={items}
                showTaskName
                card
                onTaskClick={(id) => handleTaskClick(id)}
                emptyMessage="Sin eventos con esos filtros."
              />
              {hasNextPage && (
                <div className="text-center pt-4">
                  <button
                    type="button"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="px-4 py-2 text-sm border border-stone-300 rounded hover:bg-stone-50 disabled:opacity-50 flex items-center gap-2 mx-auto"
                  >
                    {isFetchingNextPage ? (
                      <><CircleNotch size={14} weight="bold" className="animate-spin" /> Cargando más…</>
                    ) : 'Cargar más'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
